import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { getOmnibus } from '@services/omnibus';

export const execOnLoad = (initFunction, selector) => {
  if (!initFunction || !selector) return;

  if (document.readyState != 'loading') {
    [].forEach.call(document.querySelectorAll(selector), (el) => {
      initFunction(el);
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      [].forEach.call(document.querySelectorAll(selector), (el) => {
        initFunction(el);
      });
    });
  }
};

/**
 * Formats a price value according to a specified format template.
 *
 * The function converts the given value into various formatted string representations
 * based on placeholders in the `format` string. It supports multiple formats including
 * comma separators, dot separators, and space separators.
 *
 * @param {Object} params - Parameters object.
 * @param {string} [params.format=window?.dAgency?.strings?.moneyFormat] - A string template for the format.
 *        This template can contain placeholders like `{{amount}}`, `{{amount_no_decimals}}`, etc.
 * @param {number} params.value - The price value to format, provided as an integer in cents.
 *
 * @returns {string|undefined} The formatted price string based on the format template. If `value` or `format` is missing, returns `undefined`.
 */
export const formatPrice = ({ format = window?.dAgency?.strings?.moneyFormat, value }) => {
  if ((!value && value != 0) || !format) return;
  const formattedValue = (parseInt(value) / 100).toFixed(2);
  const placeholders = [
    {
      key: 'amount',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals',
      formatter: () => Math.round(formattedValue),
    },
    {
      key: 'amount_with_comma_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_comma_separator',
      formatter: () => `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    },
    {
      key: 'amount_with_apostrophe_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_space_separator',
      formatter: () => `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    },
    {
      key: 'amount_with_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_with_period_and_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
  ];

  return placeholders.reduce(
    (acc, placeholder) =>
      acc.replaceAll(new RegExp(`{{[ ]*${placeholder.key}[ ]*}}`, 'g'), placeholder.formatter(value)),
    format,
  );
};

/**
 * Initializes a number input field with increment and decrement buttons.
 *
 * @param {Object} params - The parameters for initializing the number field.
 * @param {HTMLElement} params.plusBtn - The button element used to increase the value.
 * @param {HTMLElement} params.minusBtn - The button element used to decrease the value.
 * @param {HTMLElement} params.field - The field element where the value is displayed or entered.
 *
 * @throws {Error} Will log an error if `plusBtn`, `minusBtn`, or `field` is missing.
 */
export const initNumberField = ({ plusBtn, minusBtn, field }) => {
  if (!plusBtn || !minusBtn || !field) {
    console.error('Cannot init number field, missing arguments.');
    return;
  }

  const min = field.dataset.min ? parseInt(field.dataset.min, 10) : null;
  const max = field.dataset.max ? parseInt(field.dataset.max, 10) : null;

  const updateButtonStates = () => {
    if (min !== null) {
      if (value <= min) {
        minusBtn.tagName === 'BUTTON' ? (minusBtn.disabled = true) : minusBtn.setAttribute('disabled', '');
      } else {
        minusBtn.tagName === 'BUTTON' ? (minusBtn.disabled = false) : minusBtn.removeAttribute('disabled');
      }
    }
    if (max !== null) {
      if (value >= max) {
        plusBtn.tagName === 'BUTTON' ? (plusBtn.disabled = true) : plusBtn.setAttribute('disabled', '');
      } else {
        plusBtn.tagName === 'BUTTON' ? (plusBtn.disabled = false) : plusBtn.removeAttribute('disabled');
      }
    }
  };

  const updateValue = (newValue) => {
    const clampedValue = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, newValue));
    field.tagName === 'INPUT' ? (field.value = clampedValue) : (field.innerText = clampedValue);
    if (field.tagName === 'INPUT' && value != clampedValue)
      ['input', 'change'].forEach((evt) => field.dispatchEvent(new Event(evt, { bubbles: true })));
    value = clampedValue;
    updateButtonStates();
  };

  let value = parseInt(field.tagName === 'INPUT' ? field.value : field.innerText);
  updateValue(value);

  plusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value + 1);
  });

  minusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value - 1);
  });
};

/**
 * Retrieves a CSS property value from the given container element.
 *
 * @param {HTMLElement} container - The element to retrieve the CSS property from.
 * @param {string} prop - The name of the CSS property to retrieve.
 *
 * @returns {string} The value of the given CSS property.
 *
 * @throws {Error} Will log an error if `container` or `prop` is missing.
 */
export const getCssProp = (container, prop) => {
  if (!container || !prop) {
    console.error('Cannot get css prop, missing container or prop.');
    return;
  }

  return getComputedStyle(container)[prop];
};

/**
 * Retrieves a CSS variable value from the given container element.
 *
 * @param {HTMLElement} container - The element to retrieve the CSS variable from.
 * @param {string} variable - The name of the CSS variable to retrieve.
 *
 * @returns {string} The value of the given CSS variable.
 *
 * @throws {Error} Will log an error if `container` or `variable` is missing.
 */
export const getCssVariable = (container, variable) => {
  if (!container || !variable) {
    console.error('Cannot get css variable balue, missing container or variable.');
    return;
  }

  return getComputedStyle(container).getPropertyValue(variable);
};

/**
 * Debounces an async function in a way that can be awaited.
 * By default debounce doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to debounce calls for.
 * @param wait same function as lodash.debounce's wait parameter.
 *             Call this function after this amount of time has passed since the last call.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export const asyncDebounce = (func, wait) => {
  const debounced = debounce((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise((resolve, reject) => {
      debounced(resolve, reject, args);
    });
};

/**
 * Throttles an async function in a way that can be awaited.
 * By default throttle doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to throttle calls for.
 * @param wait same function as lodash.throttle's wait parameter.
 *             Call this function at most this often.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export const asyncThrottle = (func, wait) => {
  const throttled = throttle((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise((resolve, reject) => {
      throttled(resolve, reject, args);
    });
};

/**
 * A utility function that detects whether the current device is running iOS (iPhone, iPad, or iPod).
 * It also correctly identifies modern iPad devices running iPadOS, which may report their platform as MacIntel.
 */
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;

  const isIPadPro = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (isIOS || isIPadPro) && !window.MSStream;
};

/**
 * Handles the visibility and update of the Omnibus price.
 * Fetches and displays the Omnibus value when a discount is detected,
 * including discounts added dynamically by third-party apps.
 *
 * @param {HTMLElement} container - DOM element containing the section markup.
 * @param {HTMLElement} priceWrapper - DOM element wrapping the product price, used to detect discounted prices.
 */
export const updateOmnibus = (container, priceWrapper) => {
  if (!container || !priceWrapper) {
    console.error('Cannot update Omnibus, missing container or priceWrapper.');
    return;
  }

  const omnibusContainer = container.querySelector('.omnibus__container[data-sku][data-variant-price]');
  const omnibusValue = container.querySelector('.omnibus__container [data-omnibus-value]');

  if (!omnibusContainer || !omnibusValue) return;

  const variantCompareAtPrice = parseInt(omnibusContainer.dataset.variantCompareAtPrice);
  const variantPrice = parseInt(omnibusContainer.dataset.variantPrice);

  // Check if a third‑party discount app injected a discounted price node.
  // This is for Discount Depot.
  const hasDiscountPriceApp = () => !!priceWrapper.querySelector('.axt_discounted_price');

  // Main logic to decide if Omnibus should be visible or hidden.
  const showOrHideOmnibus = (discountPriceAppPresent) => {
    getOmnibus({
      sku: omnibusContainer.dataset.sku,
      currency: omnibusContainer.dataset.currency,
      callbacks: {
        afterFetch: (omnibus) => {
          if (omnibus?.price) {
            const omnibusPrice = parseInt(omnibus.price * 100);

            const hasNativeDiscount = variantCompareAtPrice > variantPrice;

            if (discountPriceAppPresent || hasNativeDiscount) {
              omnibusValue.innerText = formatPrice({ value: omnibusPrice });
              omnibusContainer.classList.remove('hidden');
            } else {
              omnibusContainer.classList.add('hidden');
            }
          } else {
            omnibusContainer.classList.add('hidden');
          }
        },
        errorCallback: () => {
          omnibusContainer.classList.add('hidden');
        },
      },
    });
  };

  // 1) Call getOmnibus immediately, even if no external discount app is present.
  const initialDiscountPriceApp = hasDiscountPriceApp();
  showOrHideOmnibus(initialDiscountPriceApp);

  // 2) Set up MutationObserver only to react to discounts injected later by external apps.
  const observer = new MutationObserver((mutations, obs) => {
    const discountPriceApp = hasDiscountPriceApp();
    if (discountPriceApp) {
      showOrHideOmnibus(discountPriceApp);
      obs.disconnect();
    }
  });

  observer.observe(container, { childList: true, subtree: true });

  // 3) Safety timeout: stop observing after a while to avoid leaking observers.
  setTimeout(() => {
    observer.disconnect();
  }, 5000);
};
